import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import moment from 'moment';
import React from 'react';
import {Link} from 'react-router-dom';
import {
  Column,
  Filters,
  Row,
  SortingRule,
  useFilters,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';

import {ApiShareLink} from 'app/modules/Remote/ShareLink';
import * as routeUtils from 'app/utils/routeUtils';

import cs from '../PropertyOverview.module.scss';
import {InputWrapper} from '../PropertyTable';
import useFiltersPatch from '../useFiltersPatch';
import {TypeColumnFilter} from './PortfolioShareLinksColumnFilters';
import {
  CREATED_BY_COLUMN_ID,
  CREATED_COLUMN_ID,
  LINK_COLUMN_ID,
  PROPERTY_COLUMN_ID,
  SELECTION_COLUMN_ID,
  TYPE_COLUMN_ID,
  shareLinkCreator,
  shareLinkDetail,
  shareLinkMatchesSearch,
  shareLinkPropertyName,
  shareLinkTypeLabel,
} from './shareLinksTableState';
import ownCs from './styles.module.scss';

const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';

export const CANNOT_DELETE_MESSAGE =
  'Only the link’s creator or an organization owner or admin can delete it';

export function usePortfolioShareLinksTable({
  shareLinks,
  organizationId,
  projectId,
  allowSelection = true,
  initialFilters,
  initialSortBy,
}: {
  shareLinks: ApiShareLink[];
  organizationId: string;
  projectId: string;
  /** When false the selection column stays for alignment but its checkboxes are hidden. */
  allowSelection?: boolean;
  initialFilters: Filters<ApiShareLink>;
  initialSortBy: SortingRule<ApiShareLink>[];
}) {
  const columns = React.useMemo<Column<ApiShareLink>[]>(
    () => [
      {
        id: SELECTION_COLUMN_ID,
        className: cs.cellSelect,
        // Only links the user may delete are selectable, so the header
        // checkbox covers those rows rather than every row.
        Header: ({rows, selectedFlatRows, toggleRowSelected}) => {
          if (!allowSelection) {
            return null;
          }
          const deletableRows = rows.filter((row) => row.original.canDelete);
          const isAllSelected =
            deletableRows.length > 0 && deletableRows.every((row) => row.isSelected);
          return (
            <div className={classnames(cs.selectionInputWrapper, ownCs.selectionCell)}>
              <InputWrapper
                disabled={deletableRows.length === 0}
                onChange={() =>
                  deletableRows.forEach((row) => toggleRowSelected(row.id, !isAllSelected))
                }
                state={
                  isAllSelected
                    ? 'checked'
                    : selectedFlatRows.length
                      ? 'indeterminate'
                      : 'unchecked'
                }
              />
            </div>
          );
        },
        Cell: ({row}) =>
          allowSelection ? (
            <div className={classnames(cs.selectionInputWrapper, ownCs.selectionCell)}>
              <InputWrapper
                tabIndex={-1}
                disabled={!row.original.canDelete}
                title={row.original.canDelete ? undefined : CANNOT_DELETE_MESSAGE}
                onChange={() => row.toggleRowSelected(!row.isSelected)}
                state={row.isSelected ? 'checked' : 'unchecked'}
              />
            </div>
          ) : null,
      },
      {
        id: PROPERTY_COLUMN_ID,
        Header: 'Property',
        accessor: shareLinkPropertyName,
        Filter: () => null,
        filter: (rows: Row<ApiShareLink>[], _, filterValue: string) =>
          rows.filter((r) => shareLinkMatchesSearch(r.original, filterValue || '')),
        Cell: ({row, cell: {value}}) => {
          const link = row.original;
          if (link.kind === 'widget' || !link.propertyId || !link.propertyName) {
            return <span className={ownCs.unavailable}>{value}</span>;
          }
          return (
            <Link
              to={routeUtils.makeProjectDashboardUrl(organizationId, projectId, 'map', [
                link.propertyId,
              ])}
            >
              {value}
            </Link>
          );
        },
      },
      {
        id: TYPE_COLUMN_ID,
        Header: 'Type',
        className: ownCs.typeCell,
        accessor: shareLinkTypeLabel,
        Filter: TypeColumnFilter,
        filter: (rows: Row<ApiShareLink>[], [columnId], filterValue: string) =>
          filterValue ? rows.filter((r) => r.values[columnId] === filterValue) : rows,
        Cell: ({row, cell: {value}}) => {
          const detail = shareLinkDetail(row.original);
          return (
            <>
              {value}
              {detail && (
                <div className={ownCs.detail} title={detail}>
                  {detail}
                </div>
              )}
            </>
          );
        },
      },
      {
        id: CREATED_BY_COLUMN_ID,
        Header: 'Created by',
        accessor: shareLinkCreator,
      },
      {
        id: CREATED_COLUMN_ID,
        Header: 'Created',
        // Epoch milliseconds so sorting doesn't re-parse ISO strings per row.
        accessor: (link) => Date.parse(link.createdAt),
        sortType: 'basic',
        Cell: ({cell: {value}}) => moment(value).format(DISPLAY_DATE_FORMAT),
      },
      {
        id: LINK_COLUMN_ID,
        Header: 'Link',
        accessor: 'url',
        disableSortBy: true,
        Cell: ({cell: {value}}) => (
          <a className={ownCs.viewLink} href={value} target="_blank" rel="noopener noreferrer">
            View <B.Icon icon="share" size={12} />
          </a>
        ),
      },
    ],
    [organizationId, projectId, allowSelection]
  );

  const table = useTable(
    {
      columns,
      data: shareLinks,
      getRowId: (link) => link.id,
      autoResetFilters: false,
      autoResetSortBy: false,
      autoResetSelectedRows: false,
      initialState: {filters: initialFilters, sortBy: initialSortBy},
    },
    useFilters,
    useFiltersPatch,
    useSortBy,
    useRowSelect
  );

  return {table};
}
